const _ = require('lodash');
const DeferredPromise = require('./DeferredPromise');
const ConnectionService = require('./ConnectionService');

/**
 * @function assertConnection
 * @description Obtains the current connection from the ConnectionService, if available
 * @deprecated Use ConnectionService.assertConnection
 *
 * @returns {Object} connection - the ConnectionService's connection object when available
 * @throws {Error} error - an error when the connection is not established.
 */
function assertConnection() {
  return ConnectionService.assertConnection();
}

/**
 * @function waitForInvocationReaction
 * @description Creates a function that can be used to wait for a reaction based on an action invocation Id.
 *
 * @param {Object} bee - the bee to use to register for reaction callbacks.
 * @param {Function} processReactionFn - Function that processes the reaction to extract and format a response.
 * (Default: identify)
 * @param {Number} timeout - the maximum time, in milliseconds, to wait for the reaction. Default: 30000
 * @param {String} category - (optional) the reaction category to wait for.
 * @param {String} effectName - (optional) - the reaction effect name to wait for.
 *
 * @returns {Promise} promise - A promise that resolve with the processed reaction data, or is rejected on error.
 */
const waitForInvocationReaction = (
  bee,
  processReactionFn = _.identity,
  timeout = 30000,
  category = 'core',
  effectName
) => async res => {
  const invocationId = _.get(res, 'invocationId');

  let { promise, resolveFn, rejectFn } = new DeferredPromise();

  const removeCachedReaction = reaction => _.remove(bee.lastReactions, last => _.isEqual(last, reaction));

  const extractResponseFromReaction = reaction => {
    removeCachedReaction(reaction);
    const details = _.get(reaction, 'details', {});
    if (details.code) {
      rejectFn(details);
    } else {
      resolveFn(processReactionFn(reaction));
    }
  };

  // If we have an invocationId, check if the reaction is in the bee's array of last core reactions already
  const invocationLabel = effectName
    ? effectName
    : invocationId
      ? `invocation.${invocationId}`
      : undefined;
  if (invocationLabel) {
    const reaction = _.findLast(bee.lastReactions, r => r.category === category && _.includes(r.cc, invocationLabel));
    if (reaction) {
      extractResponseFromReaction(reaction);
      return promise;
    }
  }

  // Reaction is not in the existing reactions, so we wait
  let timeoutId;
  if (timeout) {
    const timeoutFn = () => rejectFn({ code: -1, message: `timeout waiting for reaction to ${invocationId}` });
    timeoutId = setTimeout(timeoutFn, timeout);
  }

  const callbackName = effectName ? `${category}.${effectName}` : `${category}:invocation.${invocationId}`;
  const callbackId = bee.reactions.setCallback(callbackName, reaction => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    extractResponseFromReaction(reaction);
  });

  try {
    return await promise;
  } finally {
    bee.reactions.removeCallback(callbackId);
  }
};

module.exports = {
  assertConnection,
  waitForInvocationReaction,
};
